/**
 * URL state for Platform ▸ Schedules: parsing the route's search params, deriving the API
 * queries from them, resolving the stats window, and sort toggling. Pure functions only, so the
 * route's `validateSearch`, the page and the tests all share one definition.
 */

import { addMillis, diffInDays, fromIsoDate, nowISO, shiftIsoDate, toUtcISO } from '~/utils/date';

import type {
  PlatformScheduleFilters,
  PlatformScheduleHealth,
  PlatformScheduleListQuery,
  PlatformScheduleRunSort,
  PlatformScheduleRunsQuery,
  PlatformScheduleRunStatus,
  PlatformScheduleSort,
  PlatformSchedulesSearch,
  PlatformSchedulesTab,
  PlatformScheduleStatus,
  PlatformUpcomingQuery,
  ScheduleWindowSpec,
  SortDirection,
  UpcomingHorizon,
  WindowPreset,
} from '../types/platform-schedules.types';

/** Rows per page on the list and run tabs (API allows 1–100). */
export const PLATFORM_SCHEDULES_PAGE_SIZE = 25;
/** Fires fetched for the Upcoming tab (API default 50, max 200). */
export const PLATFORM_UPCOMING_LIMIT = 100;
/** The API rejects a wider stats window with 400. */
export const MAX_WINDOW_DAYS = 180;
/** Debounce before a typed name search is written to the URL (and fetched). */
export const SEARCH_DEBOUNCE_MS = 300;

export const DEFAULT_TAB: PlatformSchedulesTab = 'schedules';
export const DEFAULT_PRESET: Exclude<WindowPreset, 'custom'> = '30d';
export const DEFAULT_HORIZON: UpcomingHorizon = '7d';
export const DEFAULT_LIST_SORT: PlatformScheduleSort = 'nextRunAt';
export const DEFAULT_LIST_DIRECTION: SortDirection = 'ASC';
export const DEFAULT_RUN_SORT: PlatformScheduleRunSort = 'startedAt';
export const DEFAULT_RUN_DIRECTION: SortDirection = 'DESC';

export const TABS: readonly PlatformSchedulesTab[] = ['schedules', 'runs', 'upcoming'];
export const WINDOW_PRESETS: readonly WindowPreset[] = ['7d', '30d', '90d', 'custom'];
export const UPCOMING_HORIZONS: readonly UpcomingHorizon[] = ['24h', '7d', '30d'];
export const SCHEDULE_STATUSES: readonly PlatformScheduleStatus[] = ['ACTIVE', 'PAUSED'];
export const SCHEDULE_HEALTHS: readonly PlatformScheduleHealth[] = ['HEALTHY', 'FAILING'];
export const RUN_STATUSES: readonly PlatformScheduleRunStatus[] = [
  'TRIGGERED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
  'CANCELLED',
];
export const LIST_SORTS: readonly PlatformScheduleSort[] = [
  'name',
  'subsidiary',
  'status',
  'nextRunAt',
  'lastRunAt',
  'successRate',
  'runCount',
  'failedCount',
  'productsEnriched',
  'createdAt',
];
export const RUN_SORTS: readonly PlatformScheduleRunSort[] = [
  'startedAt',
  'completedAt',
  'scheduleName',
  'subsidiary',
  'status',
];

const PRESET_DAYS: Record<Exclude<WindowPreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};
const HORIZON_HOURS: Record<UpcomingHorizon, number> = { '24h': 24, '7d': 168, '30d': 720 };
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SEARCH_LENGTH = 200;

/**
 * First click on a column sorts it in the direction a reader expects first: names and the
 * next fire ascending, counts / rates / past instants descending (worst-or-newest first).
 */
const FIRST_DIRECTION: Record<PlatformScheduleSort | PlatformScheduleRunSort, SortDirection> = {
  name: 'ASC',
  subsidiary: 'ASC',
  status: 'ASC',
  nextRunAt: 'ASC',
  lastRunAt: 'DESC',
  successRate: 'DESC',
  runCount: 'DESC',
  failedCount: 'DESC',
  productsEnriched: 'DESC',
  createdAt: 'DESC',
  startedAt: 'DESC',
  completedAt: 'DESC',
  scheduleName: 'ASC',
};

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** Accepts `'a'`, `['a','b']` or a JSON-ish repeat; drops blanks and duplicates. */
function stringList(value: unknown): string[] | undefined {
  const raw = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const items = [
    ...new Set(
      raw
        .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
        .map((v) => String(v).trim())
        .filter((v) => v !== ''),
    ),
  ];
  return items.length > 0 ? items : undefined;
}

function positiveInt(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : undefined;
}

function percent(value: unknown): number | undefined {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100
    ? Math.round(n)
    : undefined;
}

function bool(value: unknown): boolean | undefined {
  return value === true || value === 'true' ? true : undefined;
}

function isoDay(value: unknown): string | undefined {
  return typeof value === 'string' && ISO_DAY.test(value) && fromIsoDate(value) ? value : undefined;
}

/**
 * The custom range, made valid: both days present and ordered, and no wider than
 * `MAX_WINDOW_DAYS` (a hand-edited URL is clamped by moving `from` forward, keeping `to`).
 * Returns `undefined` when there is no usable range, so the page falls back to the preset.
 */
export function normalizeCustomRange(
  from: string | undefined,
  to: string | undefined,
): { from: string; to: string } | undefined {
  if (!from || !to) return undefined;
  const [start, end] = from <= to ? [from, to] : [to, from];
  // Inclusive day count: `to` is a whole day, sent as the start of the following one.
  const days = diffInDays(shiftIsoDate(start, 0), shiftIsoDate(end, 1));
  if (days <= MAX_WINDOW_DAYS) return { from: start, to: end };
  return { from: shiftIsoDate(end, -(MAX_WINDOW_DAYS - 1)).slice(0, 10), to: end };
}

/**
 * The route's `validateSearch`. Every key is optional and only kept when valid, and defaults
 * are dropped so the URL stays short (`?tab=runs`, not every param spelled out). An invalid
 * enum never reaches the API, where it would be a 400.
 */
export function parsePlatformSchedulesSearch(
  search: Record<string, unknown>,
): PlatformSchedulesSearch {
  const out: PlatformSchedulesSearch = {};

  const tab = oneOf(TABS, search.tab);
  if (tab && tab !== DEFAULT_TAB) out.tab = tab;

  const range = oneOf(WINDOW_PRESETS, search.range);
  if (range === 'custom') {
    const custom = normalizeCustomRange(isoDay(search.from), isoDay(search.to));
    if (custom) {
      out.range = 'custom';
      out.from = custom.from;
      out.to = custom.to;
    }
  } else if (range && range !== DEFAULT_PRESET) {
    out.range = range;
  }

  if (bool(search.ranInWindow)) out.ranInWindow = true;

  const subsidiaryId = stringList(search.subsidiaryId);
  if (subsidiaryId) out.subsidiaryId = subsidiaryId;
  const connector = stringList(search.connector);
  if (connector) out.connector = connector;

  const status = oneOf(SCHEDULE_STATUSES, search.status);
  if (status) out.status = status;
  const health = oneOf(SCHEDULE_HEALTHS, search.health);
  if (health) out.health = health;

  let minRate = percent(search.minRate);
  let maxRate = percent(search.maxRate);
  // The API 400s on min > max; swap rather than drop what the user asked for.
  if (minRate !== undefined && maxRate !== undefined && minRate > maxRate) {
    [minRate, maxRate] = [maxRate, minRate];
  }
  if (minRate !== undefined) out.minRate = minRate;
  if (maxRate !== undefined) out.maxRate = maxRate;

  if (typeof search.search === 'string' && search.search.trim() !== '') {
    out.search = search.search.slice(0, MAX_SEARCH_LENGTH);
  }

  const sort = oneOf(LIST_SORTS, search.sort);
  const direction = oneOf<SortDirection>(['ASC', 'DESC'], search.direction);
  if (sort && (sort !== DEFAULT_LIST_SORT || (direction ?? 'ASC') !== DEFAULT_LIST_DIRECTION)) {
    out.sort = sort;
    out.direction = direction ?? FIRST_DIRECTION[sort];
  }
  const page = positiveInt(search.page);
  if (page) out.page = page;

  const runStatus = oneOf(RUN_STATUSES, search.runStatus);
  if (runStatus) out.runStatus = runStatus;
  const runSort = oneOf(RUN_SORTS, search.runSort);
  const runDirection = oneOf<SortDirection>(['ASC', 'DESC'], search.runDirection);
  if (
    runSort &&
    (runSort !== DEFAULT_RUN_SORT || (runDirection ?? 'DESC') !== DEFAULT_RUN_DIRECTION)
  ) {
    out.runSort = runSort;
    out.runDirection = runDirection ?? FIRST_DIRECTION[runSort];
  }
  const runPage = positiveInt(search.runPage);
  if (runPage) out.runPage = runPage;

  const horizon = oneOf(UPCOMING_HORIZONS, search.horizon);
  if (horizon && horizon !== DEFAULT_HORIZON) out.horizon = horizon;

  return out;
}

// ---------------------------------------------------------------------------
// Derived state
// ---------------------------------------------------------------------------

/** The stats window the search describes (custom only when a valid range is present). */
export function windowOf(search: PlatformSchedulesSearch): ScheduleWindowSpec {
  if (search.range === 'custom' && search.from && search.to) {
    return { preset: 'custom', from: search.from, to: search.to };
  }
  return { preset: search.range ?? DEFAULT_PRESET };
}

/**
 * Resolves the window to `[from, to)` UTC instants at call time, so a preset slides forward on
 * every refetch. A custom range covers whole local days, `to` inclusive.
 */
export function resolveWindow(spec: ScheduleWindowSpec): { from: string; to: string } {
  if (spec.preset === 'custom' && spec.from && spec.to) {
    return {
      from: toUtcISO(shiftIsoDate(spec.from, 0)),
      to: toUtcISO(shiftIsoDate(spec.to, 1)),
    };
  }
  const days = PRESET_DAYS[spec.preset === 'custom' ? DEFAULT_PRESET : spec.preset];
  const now = nowISO();
  return { from: toUtcISO(addMillis(now, -days * DAY_MS)), to: toUtcISO(now) };
}

/** `[now, now + horizon)` for the Upcoming tab (the API caps it at 30 days). */
export function resolveHorizon(horizon: UpcomingHorizon): { from: string; to: string } {
  const now = nowISO();
  return { from: toUtcISO(now), to: toUtcISO(addMillis(now, HORIZON_HOURS[horizon] * HOUR_MS)) };
}

/** Filters shared by the list and `/summary`. */
export function filtersOf(search: PlatformSchedulesSearch): PlatformScheduleFilters {
  return {
    window: windowOf(search),
    subsidiaryIds: search.subsidiaryId ?? [],
    connectors: search.connector ?? [],
    status: search.status,
    health: search.health,
    search: search.search,
    ranInWindow: search.ranInWindow ?? false,
    minSuccessRate: search.minRate === undefined ? undefined : search.minRate / 100,
    maxSuccessRate: search.maxRate === undefined ? undefined : search.maxRate / 100,
  };
}

export function listQueryOf(search: PlatformSchedulesSearch): PlatformScheduleListQuery {
  return {
    ...filtersOf(search),
    sort: search.sort ?? DEFAULT_LIST_SORT,
    direction: search.direction ?? DEFAULT_LIST_DIRECTION,
    page: search.page ?? 0,
    size: PLATFORM_SCHEDULES_PAGE_SIZE,
  };
}

export function runsQueryOf(search: PlatformSchedulesSearch): PlatformScheduleRunsQuery {
  return {
    window: windowOf(search),
    subsidiaryIds: search.subsidiaryId ?? [],
    connectors: search.connector ?? [],
    status: search.runStatus,
    sort: search.runSort ?? DEFAULT_RUN_SORT,
    direction: search.runDirection ?? DEFAULT_RUN_DIRECTION,
    page: search.runPage ?? 0,
    size: PLATFORM_SCHEDULES_PAGE_SIZE,
  };
}

export function upcomingQueryOf(search: PlatformSchedulesSearch): PlatformUpcomingQuery {
  return {
    subsidiaryIds: search.subsidiaryId ?? [],
    connectors: search.connector ?? [],
    horizon: search.horizon ?? DEFAULT_HORIZON,
    limit: PLATFORM_UPCOMING_LIMIT,
  };
}

/** Keys the "Clear filters" button resets — everything except tab, window and sort. */
const FILTER_KEYS = [
  'subsidiaryId',
  'connector',
  'status',
  'health',
  'minRate',
  'maxRate',
  'search',
  'ranInWindow',
  'runStatus',
] as const satisfies readonly (keyof PlatformSchedulesSearch)[];

/** True when any narrowing filter is set — switches empty copy to "no matches". */
export function hasActiveFilters(search: PlatformSchedulesSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

/** Copies `search` without the given keys (no dynamic `delete`). */
function omitKeys(
  search: PlatformSchedulesSearch,
  keys: readonly (keyof PlatformSchedulesSearch)[],
): PlatformSchedulesSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !keys.includes(key as keyof PlatformSchedulesSearch)),
  ) as PlatformSchedulesSearch;
}

/** Clears the filters and both page indexes; keeps tab, window, horizon and sort. */
export function clearFilters(search: PlatformSchedulesSearch): PlatformSchedulesSearch {
  return omitKeys(search, [...FILTER_KEYS, 'page', 'runPage']);
}

/**
 * Applies a filter patch: drops keys set to `undefined` / empty arrays and resets both page
 * indexes, since a new filter means page one of a different result set.
 */
export function withFilterPatch(
  search: PlatformSchedulesSearch,
  patch: Partial<PlatformSchedulesSearch>,
): PlatformSchedulesSearch {
  const merged = Object.entries({ ...search, ...patch }).filter(
    ([key, value]) =>
      value !== undefined &&
      !(Array.isArray(value) && value.length === 0) &&
      key !== 'page' &&
      key !== 'runPage',
  );
  return Object.fromEntries(merged) as PlatformSchedulesSearch;
}

/**
 * Sort toggling for a header click: the active column flips direction; a new column starts in
 * its natural first direction. Returns the next `{ sort, direction }` pair.
 */
export function nextSort<S extends PlatformScheduleSort | PlatformScheduleRunSort>(
  current: { sort: S; direction: SortDirection },
  clicked: S,
): { sort: S; direction: SortDirection } {
  if (current.sort === clicked) {
    return { sort: clicked, direction: current.direction === 'ASC' ? 'DESC' : 'ASC' };
  }
  return { sort: clicked, direction: FIRST_DIRECTION[clicked] };
}

/**
 * The page to move to when `page` (0-based) is past the end of a non-empty result — a shared
 * link to page 9, or rows that disappeared since the pager was drawn. The API answers such a
 * page with no items but the real total, which would otherwise read as "no schedules" with no
 * pager to get back. `undefined` (= page one in the URL) for the first page; `null` when the
 * page is fine.
 */
export function clampPage(
  page: number,
  result: { items: readonly unknown[]; totalPages: number },
): number | undefined | null {
  if (result.items.length > 0 || result.totalPages === 0 || page < result.totalPages) return null;
  const last = result.totalPages - 1;
  return last > 0 ? last : undefined;
}
